import {
    LucideArrowRight,
    LucideChevronRight,
    LucideCircleX,
    LucideCopy,
    LucideExternalLink,
    LucidePackage,
    LucidePackageCheck,
    LucideSquarePen,
    LucideTrash2,
} from "lucide-react";
import { CustomDialog } from "../../../../../../../components/dialog/v1/CustomDialog";
import { useSelectedShoppingCartStageValueContextHook } from "../../../../contexts/SelectedShoppingCartStageContext";
import {
    useDeleteShoppingCartItemMutation,
    useDeleteShoppingCartItemsMutation,
    usePatchShoppingCartItemsMutation,
    useShoppingCartItemsQuery,
} from "../../../../Data/shoppingCartItemsReactQuery/ShoppingCartItemsReactQuery";
import styled from "styled-components";
import CustomBlockButton from "../../../../../../../components/button/CustomBlockButton";
import { useEffect, useState } from "react";
import { CustomLabelInput } from "../../../../../../../components/input/CustomLabelInput";
import { ModalViewPurchaseMemo } from "../../../weget-table/modals/modal-view-purchase-memo";
import CustomSmallButton from "../../../../../../../components/button/CustomSmallButton/CustomSmallButton";
import { customToast } from "../../../../../../../components/toast/custom-react-toastify/v1";
import { Tooltip } from "@mui/material";

const StyledBody = styled.div``;

const StyledItem = styled.div`
    .itemInfo {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .purchaseInfo {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .labelInputBox {
        input {
            border-color: #efefef;

            &:focus {
                border-color: var(--primaryColor1);
                box-shadow: 0 0 0 2px var(--primaryColor3);
            }
        }
    }

    .actionButtons {
        button {
            padding: 8px 16px;
            border-radius: 5px;
            width: auto;
            height: auto;
            font-weight: 700;

            &.ok {
                background-color: #333;
                color: #fff;
                border: none;
            }

            &.delete {
                background-color: var(--redColor1);
                color: #fff;
                border: none;
            }

            &.cancel {
                background-color: #f5f5f5;
                color: #333;
                border: none;
            }
        }
    }
`;

function sortDataByProductName(data) {
    // 원본 배열을 변경하지 않기 위해 slice()로 복사본을 만듭니다.
    const sortedData = data.slice();

    sortedData.sort((a, b) => {
        const nameA = a.productDto.name + a.productOptionDto.name;
        const nameB = b.productDto.name + b.productOptionDto.name;

        // localeCompare를 사용하여 문자열을 비교합니다.
        // 이는 한국어, 대소문자, 특수문자 등을 올바르게 처리하는 데 도움을 줍니다.
        return nameA.localeCompare(nameB);
    });

    return sortedData;
}

export function ModalShoppingCartItems({ open, onClose }) {
    const selectedShoppingCartStageValueContextHook =
        useSelectedShoppingCartStageValueContextHook();
    const selectedShoppingCartStage =
        selectedShoppingCartStageValueContextHook.selectedShoppingCartStage;

    const { data: shoppingCartItemsData, isLoading } =
        useShoppingCartItemsQuery({
            shoppingCartStageId: selectedShoppingCartStage?._id,
        });
    const mainDataProjections = shoppingCartItemsData?.data?.content;

    const [deleteAllModeOpen, setDeleteAllModeOpen] = useState(false);
    const deleteShoppingCartItemsMutation =
        useDeleteShoppingCartItemsMutation();

    const sortedDataProjections = mainDataProjections
        ? sortDataByProductName(mainDataProjections)
        : [];
    const uniqueProductDtos = sortedDataProjections
        ? sortedDataProjections?.reduce((prev, curr) => {
              if (
                  curr?.productDto &&
                  !prev.find((item) => item.id === curr?.productDto.id)
              ) {
                  prev.push(curr?.productDto);
              }
              return prev;
          }, [])
        : [];

    return (
        <>
            <CustomDialog
                open={open}
                backgroundColor="var(--backgroundField)"
                maxWidth="sm"
            >
                <CustomDialog.Header className="p-2.5 sticky top-0 bg-[var(--backgroundField)] z-10">
                    <CustomDialog.Header.Fake />
                    <CustomDialog.Header.Title>
                        {selectedShoppingCartStage?.name} 장바구니 상품 목록
                    </CustomDialog.Header.Title>
                    <CustomDialog.Header.Close onClick={onClose} />
                </CustomDialog.Header>
                <StyledBody className="flex flex-col gap-4 p-4">
                    {deleteAllModeOpen ? (
                        <>
                            <div className="border border-red-400 bg-red-50 p-4 rounded-md flex flex-col gap-4">
                                <div className="font-bold text-center">
                                    현재 장바구니의 모든 상품을
                                    삭제하시겠습니까?
                                </div>
                                <div className="mt-4 flex gap-2 justify-end">
                                    <CustomSmallButton
                                        type="button"
                                        className="bg-red-500 text-white hover:bg-red-600 text-sm px-4 py-2"
                                        onClick={() => {
                                            const body = {
                                                shoppingCartStageId:
                                                    selectedShoppingCartStage?._id,
                                            };

                                            deleteShoppingCartItemsMutation.mutate(
                                                {
                                                    body: body,
                                                },
                                                {
                                                    onSuccess: (response) => {
                                                        setDeleteAllModeOpen(
                                                            false,
                                                        );
                                                    },
                                                },
                                            );
                                        }}
                                    >
                                        삭제
                                    </CustomSmallButton>
                                    <CustomSmallButton
                                        type="button"
                                        className="text-sm px-4 py-2"
                                        onClick={() =>
                                            setDeleteAllModeOpen(false)
                                        }
                                    >
                                        취소
                                    </CustomSmallButton>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex gap-2 justify-end">
                            <CustomSmallButton
                                type="button"
                                className="bg-red-100 border border-red-400 hover:bg-red-50"
                                onClick={() => {
                                    setDeleteAllModeOpen(true);
                                }}
                            >
                                <span className="flex items-center gap-2 text-red-400">
                                    <LucideTrash2 size={12} /> 전체 삭제
                                </span>
                            </CustomSmallButton>
                            <CustomSmallButton
                                onClick={() => {
                                    const optionCodes = sortedDataProjections
                                        ?.map(
                                            (projection) =>
                                                projection?.productOptionDto
                                                    ?.code,
                                        )
                                        .join("\n");
                                    navigator.clipboard
                                        .writeText(optionCodes)
                                        .then(() => {
                                            customToast.success(
                                                "옵션코드가 복사되었습니다.",
                                            );
                                        })
                                        .catch((error) => {
                                            customToast.error(
                                                "복사 실패:",
                                                error,
                                            );
                                        });
                                }}
                            >
                                <span className="flex items-center gap-2">
                                    <LucideCopy size={12} /> 전체 옵션코드
                                </span>
                            </CustomSmallButton>
                            <CustomSmallButton
                                onClick={() => {
                                    const optionCodes = sortedDataProjections
                                        ?.map(
                                            (projection) =>
                                                projection?.shoppingCartItemDto
                                                    ?.purchaseQty,
                                        )
                                        .join("\n");
                                    navigator.clipboard
                                        .writeText(optionCodes)
                                        .then(() => {
                                            customToast.success(
                                                "수량이 복사되었습니다.",
                                            );
                                        })
                                        .catch((error) => {
                                            customToast.error(
                                                "복사 실패:",
                                                error,
                                            );
                                        });
                                }}
                            >
                                <span className="flex items-center gap-2">
                                    <LucideCopy size={12} /> 전체 수량
                                </span>
                            </CustomSmallButton>
                        </div>
                    )}
                    {uniqueProductDtos?.map((uniqueProductDto) => {
                        const dataProjections = sortedDataProjections?.filter(
                            (projection) =>
                                projection?.productDto?.id ===
                                uniqueProductDto?.id,
                        );
                        const isAllChecked = dataProjections?.every(
                            (projection) =>
                                projection?.shoppingCartItemDto?.checkedFlag,
                        );
                        return (
                            <div
                                key={uniqueProductDto?.id}
                                className="flex flex-col gap-1 border p-4 bg-white rounded-md"
                            >
                                <div className="font-bold text-lg">
                                    <span
                                        className={`${isAllChecked ? "text-green-500" : "text-black"}`}
                                    >
                                        {uniqueProductDto?.name}
                                    </span>
                                </div>
                                <div className="flex">
                                    {isAllChecked ? (
                                        <div className=" w-1 bg-green-400 ml-2 rounded-sm"></div>
                                    ) : (
                                        <div className=" w-1 bg-gray-400 ml-2 rounded-sm"></div>
                                    )}
                                    <div className="flex flex-col gap-2 w-full">
                                        {dataProjections?.map((projection) => {
                                            const shoppingCartItemDto =
                                                projection?.shoppingCartItemDto;
                                            const productOptionDto =
                                                projection?.productOptionDto;
                                            const productDto =
                                                projection?.productDto;

                                            return (
                                                <div
                                                    key={
                                                        shoppingCartItemDto?._id
                                                    }
                                                    className="flex items-center"
                                                >
                                                    {shoppingCartItemDto?.checkedFlag ? (
                                                        <div className="w-4 h-1 bg-green-400"></div>
                                                    ) : (
                                                        <div className="w-4 h-1 bg-gray-400"></div>
                                                    )}
                                                    <div className="w-full">
                                                        <ItemComponent
                                                            shoppingCartItemDto={
                                                                shoppingCartItemDto
                                                            }
                                                            productOptionDto={
                                                                productOptionDto
                                                            }
                                                            productDto={
                                                                productDto
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </StyledBody>
            </CustomDialog>
        </>
    );
}

function ItemComponent({ shoppingCartItemDto, productOptionDto, productDto }) {
    const [actionType, setActionType] = useState("VIEW"); // VIEW, EDIT, DELETE

    const [editableShoppingCartItemDto, setEditableShoppingCartItemDto] =
        useState(null);

    const patchShoppingCartItemsMutation = usePatchShoppingCartItemsMutation();
    const deleteShoppingCartItemMutation = useDeleteShoppingCartItemMutation();

    const [changePurchaseInfoModalOpen, setChangePurchaseInfoModalOpen] =
        useState(false);

    useEffect(() => {
        if (["EDIT", "DELETE"].includes(actionType)) {
            setEditableShoppingCartItemDto(shoppingCartItemDto);
        } else {
            setEditableShoppingCartItemDto(null);
        }
    }, [actionType]);

    if (actionType === "EDIT") {
        return (
            <StyledItem className="border p-2 rounded-md bg-white">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        const payload = [];

                        payload.push({
                            _id: editableShoppingCartItemDto?._id,
                            purchaseQty:
                                editableShoppingCartItemDto?.purchaseQty,
                        });

                        const body = {
                            payload: payload,
                        };

                        patchShoppingCartItemsMutation.mutate(
                            {
                                body: body,
                            },
                            {
                                onSuccess: (response) => {
                                    setActionType("VIEW");
                                },
                            },
                        );
                    }}
                >
                    <div className="itemInfo max-[568px]:w-full">
                        <div>
                            {shoppingCartItemDto?.checkedFlag ? (
                                <LucidePackageCheck
                                    size={20}
                                    cursor="pointer"
                                    className="text-green-500"
                                    onClick={() => {
                                        const body = {
                                            payload: [
                                                {
                                                    _id: shoppingCartItemDto?._id,
                                                    checkedFlag: false,
                                                },
                                            ],
                                        };
                                        patchShoppingCartItemsMutation.mutate({
                                            body: body,
                                        });
                                    }}
                                />
                            ) : (
                                <LucidePackage
                                    size={20}
                                    cursor="pointer"
                                    onClick={() => {
                                        const body = {
                                            payload: [
                                                {
                                                    _id: shoppingCartItemDto?._id,
                                                    checkedFlag: true,
                                                },
                                            ],
                                        };
                                        patchShoppingCartItemsMutation.mutate({
                                            body: body,
                                        });
                                    }}
                                />
                            )}
                        </div>
                        <div
                            style={{ fontSize: "13px", fontWeight: "bold" }}
                            className={`${shoppingCartItemDto?.checkedFlag ? "line-through" : ""}`}
                        >
                            {productDto?.name}
                        </div>
                        <div
                            style={{ fontSize: "11px", color: "#333" }}
                            className={`${shoppingCartItemDto?.checkedFlag ? "line-through" : ""}`}
                        >
                            {productOptionDto?.name}
                        </div>
                        <CustomSmallButton
                            type="button"
                            className="text-[10px] px-1.5 py-0.5 text-gray-700 bg-gray-200 hover:bg-gray-300"
                            onClick={() => {
                                navigator.clipboard
                                    .writeText(productOptionDto?.code)
                                    .then(() => {
                                        customToast.success(
                                            "옵션코드가 복사되었습니다.",
                                        );
                                    })
                                    .catch((error) => {
                                        customToast.error("복사 실패:", error);
                                    });
                            }}
                        >
                            <span className="flex items-center gap-1">
                                <LucideCopy size={10} /> 옵션코드
                            </span>
                        </CustomSmallButton>
                    </div>
                    <CustomLabelInput className="labelInputBox mt-4">
                        <CustomLabelInput.Input
                            type="number"
                            value={
                                editableShoppingCartItemDto?.purchaseQty || ""
                            }
                            autoFocus
                            onChange={(e) => {
                                setEditableShoppingCartItemDto((prev) => {
                                    return {
                                        ...prev,
                                        purchaseQty: Number(e.target.value),
                                    };
                                });
                            }}
                        />
                        <CustomLabelInput.Label>수량</CustomLabelInput.Label>
                    </CustomLabelInput>
                    <div className="flex gap-2 mt-2 justify-end actionButtons">
                        <CustomBlockButton
                            type="button"
                            className="cancel"
                            onClick={() => setActionType("VIEW")}
                        >
                            취소
                        </CustomBlockButton>
                        <CustomBlockButton type="submit" className="ok">
                            확인
                        </CustomBlockButton>
                    </div>
                </form>
            </StyledItem>
        );
    }

    if (actionType === "DELETE") {
        return (
            <StyledItem className="border p-2 rounded-md bg-white">
                <div className="text-center font-bold">
                    해당 상품을 장바구니에서 삭제하시겠습니까?
                </div>
                <div className="flex gap-2 mt-2 justify-end actionButtons">
                    <CustomBlockButton
                        type="button"
                        className="cancel"
                        onClick={() => setActionType("VIEW")}
                    >
                        취소
                    </CustomBlockButton>
                    <CustomBlockButton
                        type="button"
                        className="delete"
                        onClick={() => {
                            deleteShoppingCartItemMutation.mutate(
                                {
                                    body: {
                                        _id: editableShoppingCartItemDto?._id,
                                    },
                                },
                                {
                                    onSuccess: (response) => {
                                        setActionType("VIEW");
                                    },
                                },
                            );
                        }}
                        autoFocus
                    >
                        삭제
                    </CustomBlockButton>
                </div>
            </StyledItem>
        );
    }
    return (
        <>
            <StyledItem className="border p-2 rounded-md bg-white">
                <div
                    className="itemInfo max-[568px]:flex-col"
                    style={{ justifyContent: "space-between" }}
                >
                    <div className="itemInfo max-[568px]:w-full">
                        <div>
                            {shoppingCartItemDto?.checkedFlag ? (
                                <LucidePackageCheck
                                    size={20}
                                    cursor="pointer"
                                    className="text-green-500"
                                    onClick={() => {
                                        const body = {
                                            payload: [
                                                {
                                                    _id: shoppingCartItemDto?._id,
                                                    checkedFlag: false,
                                                },
                                            ],
                                        };
                                        patchShoppingCartItemsMutation.mutate({
                                            body: body,
                                        });
                                    }}
                                />
                            ) : (
                                <LucidePackage
                                    size={20}
                                    cursor="pointer"
                                    onClick={() => {
                                        const body = {
                                            payload: [
                                                {
                                                    _id: shoppingCartItemDto?._id,
                                                    checkedFlag: true,
                                                },
                                            ],
                                        };
                                        patchShoppingCartItemsMutation.mutate({
                                            body: body,
                                        });
                                    }}
                                />
                            )}
                        </div>
                        <div
                            style={{ fontSize: "13px", fontWeight: "bold" }}
                            className={`${shoppingCartItemDto?.checkedFlag ? "line-through" : ""}`}
                        >
                            {productDto?.name}
                        </div>
                        <div
                            style={{ fontSize: "11px", color: "#333" }}
                            className={`${shoppingCartItemDto?.checkedFlag ? "line-through" : ""}`}
                        >
                            {productOptionDto?.name}
                        </div>
                        <CustomSmallButton
                            type="button"
                            className="text-[10px] px-1.5 py-0.5 text-gray-700 bg-gray-200 hover:bg-gray-300"
                            onClick={() => {
                                navigator.clipboard
                                    .writeText(productOptionDto?.code)
                                    .then(() => {
                                        customToast.success(
                                            "옵션코드가 복사되었습니다.",
                                        );
                                    })
                                    .catch((error) => {
                                        customToast.error("복사 실패:", error);
                                    });
                            }}
                        >
                            <span className="flex items-center gap-1">
                                <LucideCopy size={10} /> 옵션코드
                            </span>
                        </CustomSmallButton>
                    </div>
                    <div className="flex items-center gap-2 max-[568px]:w-full max-[568px]:justify-end">
                        <div className="font-bold text-[13px]">
                            {shoppingCartItemDto?.purchaseQty}개
                        </div>
                        <div className={`flex items-center gap-2`}>
                            <Tooltip title={"수량복사"}>
                                <LucideCopy
                                    size={13}
                                    cursor={"pointer"}
                                    onClick={() => {
                                        navigator.clipboard
                                            .writeText(
                                                shoppingCartItemDto?.purchaseQty,
                                            )
                                            .then(() => {
                                                customToast.success(
                                                    "수량이 복사되었습니다.",
                                                );
                                            })
                                            .catch((error) => {
                                                customToast.error(
                                                    "복사 실패:",
                                                    error,
                                                );
                                            });
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title={"수정"}>
                                <LucideSquarePen
                                    size={13}
                                    cursor={"pointer"}
                                    onClick={() => setActionType("EDIT")}
                                />
                            </Tooltip>
                            <Tooltip title={"삭제"}>
                                <LucideTrash2
                                    size={13}
                                    color="var(--redColor1)"
                                    cursor={"pointer"}
                                    onClick={() => setActionType("DELETE")}
                                />
                            </Tooltip>
                        </div>
                    </div>
                </div>
                <div className="flex pl-4 mt-4 gap-2 flex-wrap items-center">
                    <div className="pr-4">
                        <CustomSmallButton
                            type="button"
                            className="text-[12px] px-2 py-1 text-white"
                            onClick={() => setChangePurchaseInfoModalOpen(true)}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }}
                                className="text-[12px] font-bold"
                            >
                                <LucideSquarePen size={12} /> 구매정보 변경
                            </div>
                        </CustomSmallButton>
                    </div>
                    {shoppingCartItemDto?.purchaseInfo && (
                        <div className="flex items-center gap-1 flex-wrap">
                            <span
                                style={{
                                    border: "1px solid #eee",
                                    padding: "2px 4px",
                                    borderRadius: "4px",
                                    fontWeight: "700",
                                    backgroundColor: "#f9f9f9",
                                }}
                                className="text-[12px]"
                            >
                                {
                                    shoppingCartItemDto?.purchaseInfo
                                        ?.purchaseRoute
                                }
                            </span>
                            <span>
                                <LucideChevronRight size={12} />
                            </span>
                            <span
                                style={{
                                    border: "1px solid #eee",
                                    padding: "2px 4px",
                                    borderRadius: "4px",
                                    fontWeight: "700",
                                    backgroundColor: "#f9f9f9",
                                }}
                                className="text-[12px]"
                            >
                                {shoppingCartItemDto?.purchaseInfo?.name}
                            </span>
                            {shoppingCartItemDto?.purchaseInfo?.purchaseUrl && (
                                <>
                                    <span>
                                        <LucideChevronRight size={12} />
                                    </span>
                                    <CustomBlockButton
                                        type="button"
                                        style={{
                                            width: "auto",
                                            height: "auto",
                                            padding: "4px 8px",
                                            borderRadius: "5px",
                                            background: "#007bff",
                                            color: "#fff",
                                            border: "none",
                                        }}
                                        onClick={() => {
                                            window.open(
                                                shoppingCartItemDto
                                                    ?.purchaseInfo?.purchaseUrl,
                                                "_blank",
                                            );
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                            }}
                                            className="text-[12px] font-bold"
                                        >
                                            <LucideExternalLink size={12} />{" "}
                                            링크
                                        </div>
                                    </CustomBlockButton>
                                </>
                            )}
                        </div>
                    )}
                </div>
                {shoppingCartItemDto?.purchaseInfo &&
                    shoppingCartItemDto?.purchaseInfo?.purchaseRoute ===
                        "1688" && (
                        <div className="pl-4 mt-2 ">
                            <div className="text-[10px]">
                                offerId:{" "}
                                {shoppingCartItemDto?.purchaseInfo?.offerId}
                            </div>
                            <div className="text-[10px]">
                                specId:{" "}
                                {shoppingCartItemDto?.purchaseInfo?.specId}
                            </div>
                        </div>
                    )}
            </StyledItem>
            {changePurchaseInfoModalOpen && (
                <ModalViewPurchaseMemo
                    open={changePurchaseInfoModalOpen}
                    onClose={() => setChangePurchaseInfoModalOpen(false)}
                    productOptionDetail={{
                        productOptionDto: productOptionDto,
                        productDto: productDto,
                    }}
                    ableToSelect={true}
                    onSelect={(selectedPurchaseInfo) => {
                        const payload = [];

                        payload.push({
                            _id: shoppingCartItemDto?._id,
                            purchaseInfo: selectedPurchaseInfo,
                            purchaseInfoUuid: selectedPurchaseInfo?.uuid,
                            nullSetter: selectedPurchaseInfo
                                ? null
                                : "PURCHASE_INFO_NULL", // 미선택시 nullSetter를 이용해서 purchaseInfo를 클리어
                        });

                        const body = {
                            payload: payload,
                        };
                        patchShoppingCartItemsMutation.mutate(
                            {
                                body: body,
                            },
                            {
                                onSuccess: (response) => {
                                    setChangePurchaseInfoModalOpen(false);
                                },
                            },
                        );
                    }}
                />
            )}
        </>
    );
}
